/* Discount code form in the cart summary. Applies codes via the Ajax Cart
   API (`POST /cart/update.js` with a comma-separated `discount` value) and
   re-renders the cart page through the Section Rendering API — line prices,
   the summary and the header cart pill all reflect the discount immediately.

   Defined as a custom element because the whole `.js-cart-summary` wrapper
   (including this element) is swapped on every cart change; connectedCallback
   re-binds the fresh DOM after each swap. */

if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('.cart-discount__form');
        this.input = this.querySelector('.cart-discount__input');
        this.error = this.querySelector('.cart-discount__error');

        this.form.noValidate = true;
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.querySelectorAll('.cart-discount__remove').forEach((button) =>
          button.addEventListener('click', this.onRemove.bind(this))
        );
      }

      get appliedCodes() {
        try {
          return JSON.parse(this.dataset.codes || '[]');
        } catch {
          return [];
        }
      }

      async onSubmit(event) {
        event.preventDefault();
        event.stopPropagation();
        const code = this.input.value.trim();
        if (!code || this.hasAttribute('aria-busy')) return;

        this.error.hidden = true;
        this.setAttribute('aria-busy', 'true');

        try {
          const cart = await this.updateDiscounts([...this.appliedCodes, code]);
          const entry = (cart.discount_codes || []).find(
            (d) => d.code.toLowerCase() === code.toLowerCase()
          );

          if (entry && entry.applicable) {
            await this.renderSections(cart);
            // `this` was replaced by the re-render — show the note on the fresh DOM
            const success = document.querySelector('.cart-discount__success');
            if (success) success.hidden = false;
          } else {
            // The store keeps even non-applicable codes on the cart — drop it again.
            await this.updateDiscounts(this.appliedCodes);
            this.error.hidden = false;
            this.input.focus();
          }
        } catch (e) {
          console.error(e);
          this.error.hidden = false;
        } finally {
          this.removeAttribute('aria-busy');
        }
      }

      async onRemove(event) {
        if (this.hasAttribute('aria-busy')) return;
        const code = event.currentTarget.dataset.code;
        this.setAttribute('aria-busy', 'true');

        try {
          const cart = await this.updateDiscounts(this.appliedCodes.filter((c) => c !== code));
          await this.renderSections(cart);
        } catch (e) {
          console.error(e);
        } finally {
          this.removeAttribute('aria-busy');
        }
      }

      updateDiscounts(codes) {
        return fetch(routes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify({ discount: codes.join(',') }),
        }).then((response) => response.json());
      }

      async renderSections(cartData) {
        const mainCartItems = document.getElementById('main-cart-items');
        const response = await fetch(
          `${routes.cart_url}?sections=${mainCartItems.dataset.id},cart-icon-bubble`
        );
        const sections = await response.json();

        const sectionHtml = new DOMParser().parseFromString(
          sections[mainCartItems.dataset.id],
          'text/html'
        );
        document
          .getElementById('main-cart-footer')
          .querySelector('.js-cart-summary').innerHTML =
          sectionHtml.querySelector('.js-cart-summary').innerHTML;
        mainCartItems.querySelector('.js-contents').innerHTML =
          sectionHtml.querySelector('.js-contents').innerHTML;

        const bubbleHtml = new DOMParser().parseFromString(
          sections['cart-icon-bubble'],
          'text/html'
        );
        document.getElementById('cart-icon-bubble').innerHTML =
          bubbleHtml.querySelector('.shopify-section').innerHTML;

        // Intentionally NOT publishing PUB_SUB_EVENTS.cartUpdate: Dawn's
        // CartItems listens for it and would re-fetch + re-render the whole
        // section a second time, wiping the success note shown right after
        // this swap. Everything affected (lines, summary, header pill) is
        // already refreshed above.
      }
    }
  );
}
